<script setup lang="ts">
import { ProjectModel } from "@xlabmedical/implant-planner";
import {
    MContainerColumn,
    MContainerGrow,
    MContainerRow,
    MContainer,
    MContainerSplit,
    MLoading,
    InputEventBus,
    OrderTypeEnum,
    removeFileExtesion,
    useAssets,
    useUserStore,
    useInput,
    error,
    warn,
    success,
    TVersionTypes,
} from "@xlabmedical/medic-web-framework";
import { useRouter } from "vue-router";
import { usePatientsStore } from "@/stores/PatientsStore";
import { CopyFileRequestModel, ProjectFileShareModel, ProjectFileType, ProjectPresctiptionTypeRequestModel, ResponseStatusEnum, Share } from "@xlabmedical/medic-api";
import RgShareProjectComponent from "@/components/RgShareProjectComponent.vue";
import { useDialog } from "primevue/usedialog";
import { useConfirm } from "primevue/useconfirm";
import { useI18n } from "vue-i18n";
import { getOrigin } from "@/constants/GlobalConstants";
import { useProductionCenterStore } from "@/stores/ProductionCenterStore";
import { useOrderStore } from "@/stores/OrderStore";
import { computed, ref } from "vue";
import { SegmentationStatus } from "@/models/SegmentationStatus";
import { ExportFileModel } from "@/models/ExportFileModel";
import RgProjectImplantList from "@/components/ProjectItem/RgProjectImplantList.vue";
import { useWindowSize } from "@vueuse/core";
import Dialog from "primevue/dialog";
import { copyFileBetweenProjectsWithRetry, getCopyErrorMessage } from "@/util/CreateOrderUtil";
import RgProjectItemInfo from "./RgProjectItemInfo.vue";
import RgProjectItemHeader from "./RgProjectItemHeader.vue";
import RgProjectItemActions from "./RgProjectItemActions.vue";
import { getWeblinkOrderEffectiveEditability, shouldOpenSharedOrderReadOnly } from "@/util/WeblinkProjectLookupUtil";

const { t } = useI18n();

const dialog = useDialog();
const input = useInput();

const api = useServerApi();

const router = useRouter();
const assets = useAssets();
const auth = useAuth();
const license = auth.getSessionLicence();
const user = ref(auth.getUser());
const hasOpenAllProjectsParam = ref(auth.hasOpenAllProjectsParam(user.value));

const enableAI = ref(false);
if (license) enableAI.value = !(license.versionType & TVersionTypes.START);
const sspEnabled = computed(() => auth.isSspEnabled());

interface Props {
    project: ProjectModel;
    exportToAlfred?: boolean;
    revisionIndex?: number;
    username?: string;
    sharedWith?: Share[];
}

const props = withDefaults(defineProps<Props>(), {
    exportToAlfred: false,
    revisionIndex: 0,
});

const multipleSelected = defineModel<boolean>("multipleSelected", { default: false });

const emits = defineEmits(["reload-patient-store", "segmentationTriggered", "exportToAlfred", "sendToWeblinkClicked", "exportClicked", "deleteClicked", "revisionApprovalClicked"]);

const confirm = useConfirm();

const userStore = useUserStore();
const orderStore = useOrderStore();

const patientStore = usePatientsStore();
const { patients } = storeToRefs(patientStore);

const productionCenterStore = useProductionCenterStore();
const { productionCenterList } = storeToRefs(productionCenterStore);

const preparingOrder = ref(false);
const preparingOrderText = ref(t("weblink.uploadingAttachmentsToOrder"));

const showImplants = ref(false);

const imageUrl = computed(() => {
    if (props.project.imageLoading || !props.project.imageBuffer) {
        return "";
    }
    return `data:image/png;base64,${props.project.imageBuffer}`;
});

const { width, height } = useWindowSize();

const isLargeScreen = computed(() => width.value > 2200);
const isTabletOrLess = computed(() => width.value < 1200);

const projectInfoMaxHeight = computed(() => (height.value < 1200 ? "14rem" : "16rem"));

function getProjectName(name: string) {
    return name.replace(".zip", "");
}

const onOpenClick = async () => {
    if (disableProject.value) {
        await onOpenBlockedClick();
        return;
    }

    const orderRestrictsEditing = !effectiveOrderEditable.value;
    const projectFileRestrictsEditing = getCurrentProjectFile()?.editable === false;
    const isReadOnly = (hasWeblinkOrder.value && orderRestrictsEditing) || isSharedOrderOwnedByAnotherUser.value || projectFileRestrictsEditing;
    const cleanQuery: Record<string, string> = {};

    if (isReadOnly) {
        cleanQuery.readOnly = "true";
    }

    if (hasWeblinkOrder.value && (orderRestrictsEditing || isSharedOrderOwnedByAnotherUser.value)) {
        const approved = await onOpenApprovalClick(isSharedOrderOwnedByAnotherUser.value);
        if (!approved) {
            return;
        }
    }

    await router.push({
        name: "ImplantPlannerProjectLoaderView",
        params: {
            projectHash: props.project.projectUUID,
            fileHash: props.project.uuid,
        },
        query: cleanQuery,
    });
};

const onOpenBlockedClick = (): Promise<void> =>
    new Promise((resolve) => {
        const resolveOnce = (() => {
            let done = false;
            return () => {
                if (done) return;
                done = true;
                resolve();
            };
        })();

        confirm.require({
            group: "approveOpenProjectConfirmation",
            header: t("implant-planner.warning"),
            message: t("web-rg.thisProjectBelongsToADifferentVendorPleaseContactSupportToRequestAccess"),
            acceptIcon: "pi pi-check",
            acceptLabel: t("shared.ok"),
            acceptClass: "p-button-sm",
            defaultFocus: "accept",
            rejectProps: { style: "display: none" },
            onHide: resolveOnce,
            accept: resolveOnce,
        });
    });

const onOpenApprovalClick = async (isSharedOrderOwnedByAnotherUser: boolean): Promise<boolean> => {
    return new Promise((resolve) => {
        confirm.require({
            group: "approveOpenProjectConfirmation",
            header: t("web-rg.projectIsReadOnly"),
            message: t(
                isSharedOrderOwnedByAnotherUser
                    ? "web-rg.youAreNotTheOwnerOfThisOrderTheProjectWillBeOpenedInReadOnlyMode"
                    : "web-rg.youCantEditThisProjectWhileAnOrderIsActiveAskYourOperatorToAllowEditing",
            ),
            acceptIcon: "pi pi-check",
            rejectIcon: "pi pi-times",
            rejectLabel: t("shared.cancel"),
            acceptLabel: t("web-rg.openReadOnlyProject"),
            acceptClass: "p-button-sm",
            rejectClass: "p-button-outlined p-button-sm",
            accept: async () => {
                resolve(true);
            },
            reject: () => {
                resolve(false);
            },
        });
    });
};

const onExportClick = () => {
    const model = {
        fileUuid: props.project.uuid,
        downloadFilename: props.project.downloadFilename,
    } as ExportFileModel;

    emits("exportClicked", model);
};

const onCreateOrderClick = async () => {
    preparingOrder.value = true;
    preparingOrderText.value = t("weblink.uploadingAttachmentsToOrder");
    let hasShownRetryInfo = false;

    try {
        const patient = patients.value.find((p) => p.patientId === props.project.patientId);

        const patientName = `${patient?.patientName}${props.project.projectDisplayName.replace(".zip", "")}` || t("web-rg.noPatientNameFound");

        const requestData = {
            notes: "",
            dateOfBirth: new Date(),
            firstName: patient?.firstName,
            lastName: patient?.lastName,
            extra: {
                patientName: patient?.firstName,
                lastName: patient?.lastName,
                patientID: patientName,
                modality: "",
                doctorID: "",
                study_instance_UID: patientName,
            },
        };

        const sourceProjectName = patient?.extra.study_instance_UID || "";
        const metadata = await patientStore.getProjectFileMetaDataById(props.project.projectFileId);

        const projectPrescriptionModel = {
            projectName: patientName,
            originProjectName: sourceProjectName,
            originProjectFileName: props.project.projectFilename,
            patientData: JSON.stringify(requestData),
        } as ProjectPresctiptionTypeRequestModel;

        const projectResponse = await patientStore.addProjectPrescriptionType(projectPrescriptionModel);

        if (projectResponse.status !== ResponseStatusEnum.OK) {
            error(`${t("web-rg.errorWhileCreatingProjectWithPrescriptionType")}: ${projectResponse.message}`, t("shared.error"));
            return;
        }

        // Primary .zip copy is required and blocks opening only until success.
        const primaryFilename = props.project.projectFilename;
        const primaryCopyModel: CopyFileRequestModel = {
            sourceProjectName,
            sourceFileName: primaryFilename,
            targetProjectName: patientName,
            targetFileName: primaryFilename,
            targetMetadata: JSON.stringify(metadata),
            targetType: ProjectFileType.ProjectAttachment,
            force: true,
        };

        let copiedFileId = 0;
        try {
            copiedFileId = await copyFileBetweenProjectsWithRetry(primaryCopyModel, (model) => patientStore.copyFileBetweenProjects(model), {
                onRetryAttempt: () => {
                    if (hasShownRetryInfo) {
                        return;
                    }

                    hasShownRetryInfo = true;
                    preparingOrderText.value = t("web-rg.revisionFileIsBeingProcessedRetrying");
                },
            });
        } catch (copyError) {
            const errorMessage = getCopyErrorMessage(copyError);
            error(`${t("shared.errorWhileCopyingFileBetweenProjects")}: ${errorMessage}`, t("shared.error"));
            console.error(t("shared.errorWhileCopyingFileBetweenProjects"), copyError);
            return;
        }

        const token = await userStore.getUserSessionToken();

        const url = `${getOrigin()}/weblink/order.html?token=${token}&fileId=${copiedFileId}&orderType=${OrderTypeEnum.ORDER_NO_ATTACHMENT}&projectId=${projectResponse.data.id}`;

        if (copiedFileId > 0) {
            const openedTab = window.open(url, "_blank", "noopener,noreferrer");
            if (openedTab) {
                openedTab.focus();
            }
        }

        // Preview copy is best-effort and must not block order opening.
        const previewFilename = `${props.project.projectFilename}.mobile.json`;
        const previewCopyModel: CopyFileRequestModel = {
            sourceProjectName,
            sourceFileName: previewFilename,
            targetProjectName: patientName,
            targetFileName: previewFilename,
            targetMetadata: JSON.stringify(metadata),
            targetType: ProjectFileType.ProjectAttachmentPreview,
            force: true,
        };

        try {
            await copyFileBetweenProjectsWithRetry(previewCopyModel, (model) => patientStore.copyFileBetweenProjects(model));
        } catch (copyError) {
            const errorMessage = getCopyErrorMessage(copyError);
            warn(`${t("shared.errorWhileCopyingFileBetweenProjects")}: ${errorMessage}`, t("shared.warning"));
            console.warn("Optional preview copy failed after order tab was opened", copyError);
        }
    } catch (caughtError) {
        const message = getCopyErrorMessage(caughtError);
        error(message, t("shared.error"));
        console.error("Create order flow failed", caughtError);
    } finally {
        preparingOrderText.value = t("weblink.uploadingAttachmentsToOrder");
        preparingOrder.value = false;
    }
};

const onShareClick = async () => {
    const projectName = props.project.weblinkOrderData?.weblinkProjectName ? props.project.weblinkOrderData.weblinkProjectName : props.project.patientName;
    const fileName = props.project.projectFilename;

    dialog.open(RgShareProjectComponent, {
        props: {
            header: t("shared.shareProjectFile"),
            style: { width: "50rem" },
            modal: true,
            draggable: false,
            dismissableMask: true,
        },
        data: {
            isFileShare: true,
        },
        emits: {
            onShareProjectFile: async (message: string, username: string, editable: boolean, anonymize: boolean, analogHoles: boolean) => {
                const data = {
                    projectName: projectName,
                    withUsername: username,
                    message: message,
                    editable: editable,
                    anonymize: anonymize,
                    analogHoles: analogHoles,
                    fileName: fileName,
                } as ProjectFileShareModel;

                const mobileData = {
                    ...data,
                    fileName: `${fileName}.mobile.json`,
                };

                const shareResponse = await patientStore.shareProjectFile(data);
                const shareResponseMobile = await patientStore.shareProjectFile(mobileData);

                if (!shareResponse.success) {
                    error(`${t("web-rg.errorWhileSharingProjectFile")}: ${shareResponse.response.data.message}`, t("shared.error"));
                }

                if (!shareResponseMobile.success) {
                    error(`${t("web-rg.errorWhileSharingProjectFile")}: ${shareResponseMobile.response.data.message}`, t("shared.error"));
                }

                const bothSuccess = shareResponse.success && shareResponseMobile.success;

                if (bothSuccess) {
                    success(t("web-rg.successfullySharedProjectFile"), t("shared.success"));
                }
            },
        },
    });
};

const onDeleteClick = async () => {
    const _patientName = props.project.weblinkOrderData?.weblinkProjectName ?? props.project.patientName;

    confirm.require({
        group: "deleteProjectItem",
        message: t("web-rg.deleteProjectConfirm", { projectName: props.project.projectFilename }).replace(/\\n/g, "\n"),
        header: t("web-rg.deleteProject"),
        icon: "pi pi-exclamation-triangle",
        acceptIcon: "pi pi-check",
        rejectIcon: "pi pi-times",
        modal: true,
        closable: true,
        closeOnEscape: true,
        dismissableMask: true,
        accept: async () => {
            if (props.project.weblinkOrderData?.weblinkProjectName) {
                await api.deleteProjectFile(props.project.weblinkOrderData?.weblinkProjectName, props.project.projectFilename);
            } else {
                await api.deleteProjectFile(props.project.patientName, props.project.projectFilename);
                await api.deleteProjectFile(_patientName, props.project.projectFilename);
                if (props.project.mobileFilename) {
                    await api.deleteProjectFile(_patientName, props.project.mobileFilename);
                }
                success(`${t("web-rg.youHaveDeletedProject")}: ${removeFileExtesion(props.project.projectFilename)}`, t("web-rg.projectDeleted"));
                confirm.close();
                emits("reload-patient-store");
            }
        },
        reject: () => {
            confirm.close();
        },
    });
};

const onUpdateOrder = () => {
    if (!effectiveOrderEditable.value) {
        return;
    }

    const pcUrl = productionCenterList.value.find((p) => p.id === props.project.weblinkOrderData?.productionCenterId)?.url;

    const name = encodeURIComponent(props.project.weblinkOrderData?.weblinkProjectName || "");
    const url = `${getOrigin()}/weblink/${pcUrl}/patients/${name}`;

    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
};

async function approveRevision() {
    if (!effectiveOrderEditable.value) {
        return;
    }

    const productionCenterId = props.project.weblinkOrderData?.productionCenterId;
    const prescriptionId = props.project.weblinkOrderData?.weblinkOrderId;
    const revisionPfId = props.project.weblinkOrderData?.revisionPfId || 0;

    if (revisionPfId === 0) {
        error(`${t("shared.revisionFileNotFound")}!`, t("shared.error"));
    }

    const response = await orderStore.acceptPrescriptionRevision(productionCenterId || 0, prescriptionId || 0, revisionPfId);

    if (response === ResponseStatusEnum.OK) {
        success(t("shared.approvedSuccessfully"), t("shared.success"));
    } else {
        error(`${t("shared.errorWhileApprovingRevision")}: ${response}`, t("shared.error"));
    }
}

const onRevisionApprovalClick = async () => {
    if (!effectiveOrderEditable.value) {
        return;
    }

    confirm.require({
        group: "approveRgConfirmation",
        header: t("shared.approveRevision"),
        message: t("weblink.areYouSureYouWantToAcceptThisRevisionForYourProject"),
        acceptIcon: "pi pi-check",
        rejectIcon: "pi pi-times",
        rejectLabel: t("shared.cancel"),
        acceptLabel: t("shared.approve"),
        acceptClass: "p-button-sm",
        rejectClass: "p-button-outlined p-button-sm",
        accept: async () => {
            await approveRevision();
        },
    });
};

const hasWeblinkOrder = computed(() => {
    return (props.project.weblinkOrderData?.weblinkOrderId || 0) > 0;
});

const isSharedOrderOwnedByAnotherUser = computed(() => shouldOpenSharedOrderReadOnly(hasWeblinkOrder.value, props.project.weblinkOrderData?.ownedByCurrentUser));

const showRevisionBtn = computed(() => {
    return hasWeblinkOrder.value && effectiveOrderEditable.value && getAwaitUserConfirmationInfo.value && props.revisionIndex === 0;
});

const showUpdateBtn = computed(() => {
    return false;
});

const hideProjectItemButtonLabels = computed(() => {
    return hasWeblinkOrder.value ? width.value < 1500 : width.value < 1450;
});

const shareNames = computed(() => {
    // Slim patient shares also aggregate file shares, which apply only to their own project.
    const patientShares = (props.sharedWith ?? []).filter((share) => share.isFileShare !== true);
    const allShares: Share[] = [...(props.project.shares ?? []), ...patientShares];
    const names = new Set<string>();

    allShares.forEach((share) => {
        const name = share.withUser?.name;
        if (name) {
            names.add(name);
        }
    });

    return Array.from(names);
});

const showSharesIcon = computed(() => {
    if (!isMine.value) {
        return true;
    }

    return shareNames.value.length > 0 && !hasWeblinkOrder.value;
});

const onOpenWeblinkOrderClick = async () => {
    const pcUrl = productionCenterList.value.find((p) => p.id === props.project.weblinkOrderData?.productionCenterId)?.url;

    const routeUrl = router.resolve({
        name: "OpenWeblinkOrder",
        params: {
            projectName: props.project.weblinkOrderData?.weblinkProjectName,
            productionCenterName: pcUrl,
        },
    }).href;

    window.open(routeUrl, "_blank", "noopener");
};

const getAwaitUserConfirmationInfo = computed(() => {
    const awaitUserConfirmation = props.project.weblinkOrderData?.awaitUserConfirmation;

    if (awaitUserConfirmation !== undefined) {
        return awaitUserConfirmation;
    }
    return false;
});

const showSegmentationButton = computed(() => {
    return (
        (props.project.segmentationStatus === SegmentationStatus.NOT_STARTED ||
            props.project.segmentationStatus === SegmentationStatus.FAILED ||
            !props.project.segmentationInvokedByUser) &&
        enableAI.value &&
        sspEnabled.value
    );
});

const inSegmentationProcess = computed(() => {
    return props.project.segmentationStatus === SegmentationStatus.IN_PROCESS && props.project.segmentationInvokedByUser;
});

const onSegmentClick = () => {
    emits("segmentationTriggered", props.project.id);
};

const sharedWithText = computed(() => {
    const sharedBy = `${t("web-rg.sharedBy")} ${props.project?.uploadedBy?.name}`;

    if (!isMine.value && shareNames.value.length > 0) {
        return `${sharedBy} | ${t("web-rg.sharedWith")} ${shareNames.value.join(", ")}`;
    }

    if (shareNames.value.length > 0) {
        return `${t("web-rg.sharedWith")} ${shareNames.value.join(", ")}`;
    }
    if (!isMine.value) {
        return sharedBy;
    }

    return t("web-rg.noShares");
});

type ExtraMenuTemplateItem = {
    label: string;
    icon?: string;
    command?: () => void;
    disabled?: boolean;
    checked?: boolean;
};

const editableAsProInProgress = ref(false);
const editableAsProOverride = ref<boolean | null>(null);

const getCurrentProjectFile = () => {
    return (
        props.project.projectFileList?.find((file) => file.id === props.project.projectFileId) ??
        props.project.projectFileList?.find((file) => file.name === props.project.projectFilename)
    );
};

const effectiveOrderEditable = computed(() => getWeblinkOrderEffectiveEditability(props.project.weblinkOrderData?.allowsUserEditing, getCurrentProjectFile()?.editable));

const isProjectEditableAsPro = computed(() => {
    if (editableAsProOverride.value !== null) {
        return editableAsProOverride.value;
    }

    return getCurrentProjectFile()?.editableAsPro ?? false;
});

const onToggleProjectEditableAsPro = async () => {
    if (editableAsProInProgress.value) {
        return;
    }

    const editable = !isProjectEditableAsPro.value;
    editableAsProInProgress.value = true;

    try {
        await patientStore.setProjectFileEditableAsPro({
            projectName: props.project.patientName,
            fileName: props.project.projectFilename,
            editable,
        });
        editableAsProOverride.value = editable;
    } catch (err) {
        const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (err as { message?: string })?.message ?? t("shared.error");
        error(errorMessage, t("shared.error"));
    } finally {
        editableAsProInProgress.value = false;
    }
};

const transferOwnershipInProgress = ref(false);
let validateTransferOwnershipInput = false;
const transferOwnershipRequiredMessage = `${t("web-rg.withUserEmail")} ${t("shared.isRequired")}`;

InputEventBus.on("validate-input", (event: { _name: string; target: { value: string } }) => {
    if (!validateTransferOwnershipInput) {
        return;
    }

    const isValid = event.target.value.trim() !== "";
    InputEventBus.emit("validate-result", {
        valid: isValid,
        message: isValid ? "" : transferOwnershipRequiredMessage,
    });
});

const onTransferOwnershipConfirm = async () => {
    if (transferOwnershipInProgress.value) {
        return;
    }

    let response: string | null;
    validateTransferOwnershipInput = true;

    try {
        response = await input.input({
            message: t("web-rg.enterTheEmailOfTheUserYouWishToTransferTheOwnershipTo"),
            acceptLabel: t("shared.ok"),
            rejectLabel: t("shared.cancel"),
            title: t("web-rg.transferOwnership"),
            invalid: true,
            invalidMsg: transferOwnershipRequiredMessage,
        });
    } finally {
        validateTransferOwnershipInput = false;
    }

    if (response == undefined) {
        return;
    }

    transferOwnershipInProgress.value = true;

    try {
        await patientStore.setProjectOwner({
            projectName: props.project.patientName,
            owner: response.trim(),
        });

        success(t("web-rg.projectOwnershipTransferredSuccessfully"), t("shared.success"));
        emits("reload-patient-store");
    } catch (err) {
        const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (err as { message?: string })?.message ?? t("shared.error");
        error(t("web-rg.errorWhileTransferringOwnership", { errorMessage }), t("shared.error"));
    } finally {
        transferOwnershipInProgress.value = false;
    }
};

const isMine = computed(() => props.project?.uploadedBy?.username === user.value?.username);

const disableProject = computed(() => {
    const isCurrentVendorProject = patientStore.isProjectFromCurrentVendor(props.project.versionCompany);

    return !isCurrentVendorProject && !hasOpenAllProjectsParam.value;
});

const isProjectActionDisabled = computed(() => {
    return props.project.imageLoading || disableProject.value;
});

const itemsExtra = computed(() => {
    const items: ExtraMenuTemplateItem[] = [
        {
            label: t("shared.export"),
            icon: assets.getAssetUrl("/icons/rg_upgrade_rotated.svg"),
            command: onExportClick,
        },
    ];

    if (isMine.value && !hasWeblinkOrder.value) {
        items.push({
            label: t("web-rg.transferOwnership"),
            icon: "pi pi-user-edit",
            command: onTransferOwnershipConfirm,
        });
    }

    if (auth.hasEditableAsPro()) {
        items.push({
            label: t("web-rg.enableProEditing"),
            command: onToggleProjectEditableAsPro,
            checked: isProjectEditableAsPro.value,
            disabled: editableAsProInProgress.value,
        });
    }

    if (sspEnabled.value) {
        items.push({
            label: t("web-rg.segment"),
            icon: assets.getAssetUrl("/icons/toolbar_icons/ik_xray_28.svg"),
            command: onSegmentClick,
            disabled: !showSegmentationButton.value,
        });
    }

    return [{ items }];
});

const toggleProjectInfo = () => {
    showImplants.value = !showImplants.value;
};
</script>

<template>
    <Dialog v-model:visible="preparingOrder" class="preparing-dialog" :closable="false" :draggable="false" modal :header="$t('web-rg.preparingOrder')">
        <m-loading :strokeWidth="5">
            <span>{{ preparingOrderText }}</span>
        </m-loading>
    </Dialog>
    <MContainerRow v-if="isTabletOrLess" class="rg-project-item-poc h-full">
        <MContainerRow class="items-center" style="max-height: 50px">
            <RgProjectItemHeader
                :projectName="getProjectName(project.projectFilename)"
                :hasWeblinkOrder="hasWeblinkOrder"
                :isLargeScreen="isLargeScreen"
                :showSharesIcon="showSharesIcon"
                :sharedWithText="sharedWithText"
                @toggle-project-info="toggleProjectInfo"
                @open-weblink-order="onOpenWeblinkOrderClick" />
        </MContainerRow>
        <MContainerRow v-if="isTabletOrLess">
            <MContainer :class="[!isLargeScreen ? 'h-full img-card-size-poc-ipad' : '']" style="width: 40% !important">
                <div v-if="!props.project.imageLoading" class="relative w-full h-full">
                    <img
                        :src="imageUrl"
                        :class="[
                            'w-full h-full object-contain left-bottom-radius img-card-size-poc-ipad',
                            multipleSelected && !hasWeblinkOrder ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer',
                        ]"
                        style="object-fit: cover"
                        alt=""
                        draggable="false"
                        @click="onOpenClick" />
                </div>
                <Skeleton v-else class="skeleton-card-size-poc-ipad left-radius" />
            </MContainer>
            <MContainerSplit>
                <MContainerColumn class="w-full pt-2" :class="[multipleSelected && !hasWeblinkOrder ? 'opacity-50 cursor-not-allowed pointer-events-none' : '']">
                    <MContainerRow class="h-full overflow-y-auto" :style="{ maxHeight: projectInfoMaxHeight }">
                        <RgProjectItemInfo v-if="!showImplants" :project="props.project" :hasWeblinkOrder="hasWeblinkOrder" :effective-editable="effectiveOrderEditable" />
                        <RgProjectImplantList v-else :implant-list="project.implantList" :isPopover="true" />
                    </MContainerRow>
                    <Divider />
                    <RgProjectItemActions
                        style="max-height: 3rem !important; margin-top: -1rem !important"
                        :isLargeScreen="isLargeScreen"
                        :allowsUserEditing="effectiveOrderEditable"
                        :showUpdateBtn="showUpdateBtn"
                        :showRevisionBtn="showRevisionBtn"
                        :hideProjectItemButtonLabels="hideProjectItemButtonLabels"
                        :openButtonDataCy="`openProjectButton-${getProjectName(project.projectFilename)}`"
                        :openButtonDisabled="props.project.imageLoading"
                        :inSegmentationProcess="inSegmentationProcess"
                        :hasWeblinkOrder="hasWeblinkOrder"
                        :isProjectActionDisabled="isProjectActionDisabled"
                        :itemsExtra="itemsExtra"
                        @open-click="onOpenClick"
                        @delete-click="onDeleteClick"
                        @share-click="onShareClick"
                        @create-order-click="onCreateOrderClick"
                        @update-order-click="onUpdateOrder"
                        @revision-approval-click="onRevisionApprovalClick" />
                </MContainerColumn>
            </MContainerSplit>
        </MContainerRow>
    </MContainerRow>
    <MContainerRow v-else class="rg-project-item-poc">
        <MContainerSplit :class="[!isLargeScreen ? 'h-full' : '']">
            <div v-if="!props.project.imageLoading" class="relative w-full h-full">
                <img
                    :src="imageUrl"
                    :class="[
                        'w-full h-full object-contain left-radius img-card-size-poc',
                        multipleSelected && !hasWeblinkOrder ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer',
                    ]"
                    style="object-fit: cover"
                    alt=""
                    draggable="false"
                    @click="onOpenClick" />
            </div>
            <Skeleton v-else class="skeleton-card-size-poc left-radius" />
        </MContainerSplit>
        <MContainerSplit>
            <MContainerColumn class="w-full pt-2 gap-6" :class="[multipleSelected && !hasWeblinkOrder ? 'opacity-50 cursor-not-allowed pointer-events-none' : '']">
                <RgProjectItemHeader
                    :projectName="getProjectName(project.projectFilename)"
                    :hasWeblinkOrder="hasWeblinkOrder"
                    :isLargeScreen="isLargeScreen"
                    :showSharesIcon="showSharesIcon"
                    :sharedWithText="sharedWithText"
                    @toggle-project-info="toggleProjectInfo"
                    @open-weblink-order="onOpenWeblinkOrderClick" />
                <MContainerRow class="h-full overflow-y-auto" :style="{ maxHeight: projectInfoMaxHeight }">
                    <RgProjectItemInfo v-if="!showImplants" :project="props.project" :hasWeblinkOrder="hasWeblinkOrder" :effective-editable="effectiveOrderEditable" />
                    <RgProjectImplantList v-else :implant-list="project.implantList" :isPopover="true" />
                </MContainerRow>
                <MContainerGrow class="-mt-2 rg-border-bottom" />
                <RgProjectItemActions
                    :isLargeScreen="isLargeScreen"
                    :allowsUserEditing="effectiveOrderEditable"
                    :showUpdateBtn="showUpdateBtn"
                    :showRevisionBtn="showRevisionBtn"
                    :hideProjectItemButtonLabels="hideProjectItemButtonLabels"
                    :openButtonDataCy="`openProjectButton-${getProjectName(project.projectFilename)}`"
                    :openButtonDisabled="props.project.imageLoading"
                    :inSegmentationProcess="inSegmentationProcess"
                    :hasWeblinkOrder="hasWeblinkOrder"
                    :isProjectActionDisabled="isProjectActionDisabled"
                    :itemsExtra="itemsExtra"
                    @open-click="onOpenClick"
                    @delete-click="onDeleteClick"
                    @share-click="onShareClick"
                    @create-order-click="onCreateOrderClick"
                    @update-order-click="onUpdateOrder"
                    @revision-approval-click="onRevisionApprovalClick" />
            </MContainerColumn>
        </MContainerSplit>
    </MContainerRow>
</template>

<style>
.rg-shared-with-tooltip-project.p-tooltip {
    max-width: min(36rem, calc(100vw - 2rem));
    z-index: 1100 !important;
}

.rg-shared-with-tooltip-project .p-tooltip-text {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
}

@media (max-width: 768px) {
    .rg-shared-with-tooltip-project.p-tooltip {
        max-width: 85vw;
    }
}
</style>

<style scoped>
.preparing-dialog {
    width: 25vw;
}

:deep(.m-u-button > i) {
    padding-top: 1rem !important;
}

:deep(.m-u-button > i + span) {
    margin-top: 0.45rem !important;
}

@media (max-width: 2000px) {
    .preparing-dialog {
        width: 35vw;
    }
}
</style>
